import { ref, computed, provide, onMounted } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useFormClient } from '~/composables/useFormClient';
import {
    collectPrefillValues,
    constructFormState,
    validateFormConfig,
    type FormInputs,
    type FormState,
    type StoryBlokBasicFormConfig,
    FormProcessState
} from '~/utils/formUtils';
import { validateFormInputs } from '~/utils/validator';
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';
import type { StoryPage } from '~/types/storyPage';

/**
 * Composable to handle form state, validation and submission (AJAX or native).
 * @param blok - Storyblok block containing form settings (ajaxRequest, submitAddress, method, alignment)
 * @param inputConfig - Computed array of basic form field configurations
 */
export function useFormHandler(
    blok: FormGroupStoryblok | FormGroupMultistepStoryblok,
    inputConfig: ComputedRef<StoryBlokBasicFormConfig[]>,
    captchaRef: Ref<HTMLDivElement | null> = ref(null)
) {
    const formRef = ref<HTMLFormElement | null>(null);
    const config = useRuntimeConfig();
    const baseUrl = config.public.baseUrl;

    const formClient = useFormClient();
    const { lang, country } = useLanguage();

    const formState = ref<FormState>({
        formInputs: {},
        processState: FormProcessState.DEFAULT
    });

    const storyPage = useState<StoryPage>('story-page');

    watch([captchaRef, lang], () => {
        if (!captchaRef?.value) return;

        try {
            const sitekey = config.public.friendlyCaptcha.sitekey;
            const apiEndpoint = config.public.friendlyCaptcha.apiEndpoint;

            if (!sitekey) {
                console.error('Friendly Captcha is not properly configured.');
            }

            const sdk: FriendlyCaptchaSDK = new FriendlyCaptchaSDK();

            sdk.createWidget({
                ...(apiEndpoint?.length > 0 ? { apiEndpoint } : {}),
                ...(lang.value ? { language: lang.value } : {}),
                element: captchaRef.value,
                sitekey,
                theme: 'light'
            });

            captchaRef.value.addEventListener('frc:widget.complete', (event) => {
                formState.value.formInputs['frc-captcha-response'] = {
                    value: event.detail.response
                };
            });

            captchaRef.value.addEventListener('frc:widget.error', (event) => {
                console.error('Widget ran into an error:', event.detail.error);
            });

            captchaRef.value.addEventListener('frc:widget.expire', function () {
                console.warn('The widget expired because the user waited too long');
            });
        } catch (e) {
            console.error(e);
        }
    });

    const formInputs = computed<FormInputs>(() => formState.value.formInputs);

    const hasRequiredFields = computed(() => inputConfig.value.some((cfg) => cfg.required));

    const alignmentClass = computed(() => {
        if (blok.alignment === 'center') return '--align-center';
        if (blok.alignment === 'right') return '--align-right';
        return '';
    });

    const prefillValues = computed(() => collectPrefillValues(new URLSearchParams(useRequestURL().search)));

    const initialState = computed(() => constructFormState(inputConfig.value, prefillValues.value));
    if (!validateFormConfig(inputConfig.value, initialState.value)) {
        formState.value.processState = FormProcessState.INVALID;
    }
    formState.value.formInputs = initialState.value;

    const errorCount = computed(() =>
        Object.values(formInputs.value).reduce((sum, input) => sum + (input.errors?.length || 0), 0)
    );

    const hasErrors = computed(() => errorCount.value > 0);

    const removeQueryString = async () => {
        if (!import.meta.client) return;
        if (window.location.href.includes('openZeegPop')) return;

        await nextTick();

        const url = new URL(window.location.href);
        if (!url.search) return;

        url.search = '';

        history.replaceState(history.state, '', url.toString());
    };
    onMounted(removeQueryString);

    provide<Ref<FormInputs>>('formInputs', formInputs);
    // Detect if the form contains file inputs to decide on multipart/form-data submission
    const hasFileInputs = computed(() => !!formRef.value?.querySelector('input[type="file"]'));

    const submitAjaxForm = async () => {
        const { submitAddress } = blok;
        let { method } = blok;
        if (!method) method = 'GET';

        if (!submitAddress?.trim()) {
            formState.value.processState = FormProcessState.ERROR;
            return;
        }

        const actionUrl =
            submitAddress.startsWith('http://') || submitAddress.startsWith('https://')
                ? new URL(submitAddress)
                : new URL(submitAddress, baseUrl);

        if (
            actionUrl.origin === new URL(baseUrl).origin &&
            actionUrl.pathname.startsWith('/api/forms/') &&
            !actionUrl.pathname.startsWith('/api/forms/salesforce')
        ) {
            actionUrl.searchParams.set('locale', lang.value);
            actionUrl.searchParams.set('country', country.value);
        }

        const jsonBody = Object.entries(formState.value.formInputs).reduce(
            (body, [key, { value }]) => ({ ...body, [key]: value }),
            {} as Record<string, string | boolean>
        );

        const [isValid, updatedState] = validateFormInputs(formState.value.formInputs, inputConfig.value);
        formState.value.formInputs = { ...formState.value.formInputs, ...updatedState };
        if (!isValid) return;

        try {
            let response;
            if (hasFileInputs.value) {
                const formEl = formRef.value!;
                const formData = new FormData(formEl);
                response = await formClient('POST', actionUrl, formData);
            } else {
                response = await formClient(method, actionUrl, jsonBody);
            }
            const { status } = response;
            if (status >= 200 && status < 400) {
                formState.value.processState = FormProcessState.SUCCESS;
            } else {
                formState.value.processState = FormProcessState.ERROR;
            }
        } catch (e) {
            console.error(e);
            // On error, push state to URL for fallback
            const url = new URL(window.location.href);
            url.search = new URLSearchParams(Object.entries(jsonBody).map(([k, v]) => [k, String(v)])).toString();
            history.pushState({}, '', url.toString());
            formState.value.processState = FormProcessState.ERROR;
        }
    };

    /**
     * Submits the form data after validating the inputs and updating the form state.
     * Depending on the configuration, it either submits via an AJAX request or directly submits the form element.
     *
     * @param {string | null} [storyUuid=null] - An optional unique identifier for the story. If not provided,
     *                                           it defaults to the UUID from the `storyPage` or an empty string.
     *
     * @returns {Promise<void>} A promise that resolves when the form submission process completes.
     */
    const submitForm = async (storyUuid: string | null = null): Promise<void> => {
        const [isValid, updatedState] = validateFormInputs(formState.value.formInputs, inputConfig.value);
        formState.value.formInputs = {
            ...formState.value.formInputs,
            ...updatedState,
            storyUuid: {
                value: storyUuid ?? storyPage.value?.uuid ?? ''
            }
        };

        if (!isValid) return;

        if (blok.ajaxRequest) {
            await submitAjaxForm();
        } else {
            formRef.value?.submit();
        }
    };

    return {
        formRef,
        formState,
        formInputs,
        hasRequiredFields,
        alignmentClass,
        errorCount,
        hasErrors,
        submitAjaxForm,
        submitForm
    };
}
